import { Link } from "react-router-dom";
import { ArrowRight, Heart, Flame, UtensilsCrossed, BedDouble } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMG } from "@/data/content";

const SPACES = [
  { icon: Heart, title: "The Courtyard Lawn", copy: "Ceremonies framed by stone cottages and open sky, with the wild pond a short wander away for golden-hour portraits." },
  { icon: UtensilsCrossed, title: "Feasting Barn & Long Tables", copy: "Long-table dining under festoon lights — local seafood, fire-cooked feasts and toasts that run long into the evening." },
  { icon: Flame, title: "Fire Pits & Hot Tubs", copy: "When the dancing winds down, the fires are lit. Blankets, hot tubs under some of Wales' darkest skies, and one more story." },
  { icon: BedDouble, title: "Everyone Sleeps Over", copy: "Twenty bedrooms across eight cottages and the farmhouse — up to 40 of your favourite people, all under one estate." },
];

export default function Weddings() {
  return (
    <main data-testid="weddings-page">
      <section className="relative flex min-h-[75vh] items-end overflow-hidden bg-[#1E1610]">
        <img src={IMG.weddingBarn} alt="A rustic barn wedding at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1610] via-[#1E1610]/55 to-[#1E1610]/20" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#1E1610]/70 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-40 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow light>Weddings at the Plas</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#F4F1EA] sm:text-5xl lg:text-6xl">
              A wedding weekend that feels like a village of your own.
            </h1>
            <p className="mt-5 max-w-xl text-[#F4F1EA]/75">
              Exclusive use of the whole estate — ceremony on the lawn, feasting under festoon lights, dancing by fire
              light, and everyone you love sleeping over.
            </p>
            <Link
              to="/enquire"
              data-testid="weddings-hero-enquire"
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-[#B85D43] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56] hover:text-[#1E1610]"
            >
              Start planning
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section data-testid="weddings-spaces" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Reveal>
          <Eyebrow>The day, your way</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl">
            Rustic to the bones, luxury where it counts.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {SPACES.map(({ icon: Icon, title, copy }, i) => (
            <Reveal key={title} delay={(i % 2) * 0.1}>
              <div className="h-full rounded-3xl border border-[#5A4632]/15 bg-[#F3EFEA] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#5A4632] text-[#C89D56]">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-[#241A12]">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#7A6A58]">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="weddings-takeover" className="bg-[#1E1610] py-20 text-[#F4F1EA] lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
          <Reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={IMG.community} alt="Guests feasting together outdoors" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow light>The weekend takeover</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight sm:text-4xl">
              Friday to Monday. Every cottage, every fire, all yours.
            </h2>
            <p className="mt-6 leading-relaxed text-[#F4F1EA]/70">
              Arrive to welcome feasts on Friday, wed on Saturday with the whole estate as your stage, and slow-brunch
              Sunday away by the pond. No venue turnovers, no curfews imposed by anyone but the goats.
            </p>
            <ul className="mt-7 space-y-3 text-sm text-[#F4F1EA]/75">
              {[
                "Exclusive use — 8 cottages, farmhouse & all shared spaces",
                "Sleeps up to 40 guests across 20 bedrooms",
                "Dog fields all around — four-legged guests welcome",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C89D56]" />
                  {t}
                </li>
              ))}
            </ul>
            <Link
              to="/enquire"
              data-testid="weddings-takeover-enquire"
              className="group mt-9 inline-flex items-center gap-3 rounded-full border border-[#C89D56]/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#C89D56] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56] hover:text-[#1E1610]"
            >
              Enquire about your date
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="weddings-moment" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Reveal className="relative overflow-hidden rounded-[2rem]">
          <img src={IMG.laughing} alt="Friends laughing together at a celebration" className="h-[420px] w-full object-cover" />
          <div className="absolute inset-0 flex items-end bg-gradient-to-t from-[#1E1610]/85 to-transparent p-8 sm:p-12">
            <p className="max-w-lg font-serif text-2xl italic leading-snug text-[#F4F1EA] sm:text-3xl">
              "It didn't feel like a venue. It felt like our place — we just got to keep it for the weekend."
            </p>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
